// components/integrations/ZohoConfigModal.jsx
import React, { useState, useEffect } from "react";
import { X, Save, RefreshCw, AlertCircle, CheckCircle } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const ZohoConfigModal = ({ isOpen, onClose, integration, onUpdate }) => {
  const { isDarkMode } = useTheme();
  const [syncSettings, setSyncSettings] = useState({
    syncLeads: false,
    syncAccounts: false,
    syncContacts: false,
    syncDeals: false,
    syncCases: false,
    syncTasks: false,
    syncInterval: "hourly",
    autoSync: true,
  });

  const [fieldMappings, setFieldMappings] = useState({
    leads: [],
    accounts: [],
    contacts: [],
    deals: [],
    cases: [],
    tasks: [],
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen && integration) {
      loadConfiguration();
    }
  }, [isOpen, integration]);

  const loadConfiguration = async () => {
    setLoading(true);
    setError(null);
    try {
      if (integration.syncSettings) {
        setSyncSettings(integration.syncSettings);
      }
      if (integration.fieldMappings) {
        setFieldMappings(integration.fieldMappings);
      }
    } catch (err) {
      console.error("Error loading configuration:", err);
      setError("Failed to load configuration");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      await apiService.updateZohoConfig({
        syncSettings,
        fieldMappings,
      });

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onUpdate();
        onClose();
      }, 2000);
    } catch (err) {
      console.error("Error saving configuration:", err);
      setError(err.response?.data?.message || "Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleSyncSettingChange = (key, value) => {
    setSyncSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden border ${
          isDarkMode
            ? "bg-[#0C0E12] border-[#373A41]"
            : "bg-white border-gray-200"
        }`}
      >
        {/* Header */}
        <div
          className={`p-6 border-b flex items-center justify-between ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div>
            <h3
              className={`text-lg font-semibold mb-1 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Zoho CRM Configuration
            </h3>
            <p
              className={`text-sm ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              Configure sync settings and field mappings
            </p>
          </div>
          <button
            onClick={onClose}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-180px)] p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw
                className={`w-8 h-8 animate-spin ${
                  isDarkMode ? "text-[#7F56D9]" : "text-purple-600"
                }`}
              />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Error Message */}
              {error && (
                <div
                  className={`rounded-lg p-4 flex items-start gap-3 ${
                    isDarkMode
                      ? "bg-red-900/20 border border-red-900/50"
                      : "bg-red-50 border border-red-200"
                  }`}
                >
                  <AlertCircle
                    className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                      isDarkMode ? "text-red-400" : "text-red-600"
                    }`}
                  />
                  <div>
                    <div
                      className={`font-medium ${
                        isDarkMode ? "text-red-400" : "text-red-600"
                      }`}
                    >
                      Error
                    </div>
                    <div
                      className={`text-sm ${
                        isDarkMode ? "text-red-300" : "text-red-500"
                      }`}
                    >
                      {error}
                    </div>
                  </div>
                </div>
              )}

              {/* Success Message */}
              {success && (
                <div
                  className={`rounded-lg p-4 flex items-start gap-3 ${
                    isDarkMode
                      ? "bg-green-900/20 border border-green-900/50"
                      : "bg-green-50 border border-green-200"
                  }`}
                >
                  <CheckCircle
                    className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                      isDarkMode ? "text-green-400" : "text-green-600"
                    }`}
                  />
                  <div>
                    <div
                      className={`font-medium ${
                        isDarkMode ? "text-green-400" : "text-green-600"
                      }`}
                    >
                      Success
                    </div>
                    <div
                      className={`text-sm ${
                        isDarkMode ? "text-green-300" : "text-green-500"
                      }`}
                    >
                      Configuration saved successfully
                    </div>
                  </div>
                </div>
              )}

              {/* Module Selection */}
              <div
                className={`border rounded-lg p-5 ${
                  isDarkMode
                    ? "bg-[#1a1d23] border-gray-700"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <h4
                  className={`font-medium mb-4 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Modules to Sync
                </h4>
                <div className="space-y-3">
                  {[
                    {
                      key: "syncLeads",
                      label: "Leads",
                      description: "Sync lead records from Zoho CRM",
                    },
                    {
                      key: "syncAccounts",
                      label: "Accounts",
                      description: "Sync account/company records",
                    },
                    {
                      key: "syncContacts",
                      label: "Contacts",
                      description: "Sync contact records",
                    },
                    {
                      key: "syncDeals",
                      label: "Deals",
                      description: "Sync deal/opportunity records",
                    },
                    {
                      key: "syncCases",
                      label: "Cases",
                      description: "Sync support case records",
                    },
                    {
                      key: "syncTasks",
                      label: "Tasks",
                      description: "Sync task and activity records",
                    },
                  ].map((module) => (
                    <label
                      key={module.key}
                      className={`flex items-start gap-3 p-3 rounded-lg transition-colors cursor-pointer ${
                        isDarkMode
                          ? "bg-[#0C0E12] hover:bg-[#22262F]"
                          : "bg-white hover:bg-gray-100"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={syncSettings[module.key]}
                        onChange={(e) =>
                          handleSyncSettingChange(module.key, e.target.checked)
                        }
                        className={`mt-1 w-4 h-4 rounded focus:ring-[#7F56D9] focus:ring-offset-0 ${
                          isDarkMode
                            ? "border-gray-600 text-[#7F56D9] bg-[#1a1d23]"
                            : "border-gray-300 text-purple-600 bg-white"
                        }`}
                      />
                      <div className="flex-1">
                        <div
                          className={`font-medium ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {module.label}
                        </div>
                        <div
                          className={`text-sm ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          {module.description}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Sync Settings */}
              <div
                className={`border rounded-lg p-5 ${
                  isDarkMode
                    ? "bg-[#1a1d23] border-gray-700"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <h4
                  className={`font-medium mb-4 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Sync Settings
                </h4>

                <div className="space-y-4">
                  {/* Auto Sync */}
                  <label
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      isDarkMode ? "bg-[#0C0E12]" : "bg-white"
                    }`}
                  >
                    <div>
                      <div
                        className={`font-medium ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        Automatic Sync
                      </div>
                      <div
                        className={`text-sm ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Automatically sync data on a schedule
                      </div>
                    </div>
                    <button
                      onClick={() =>
                        handleSyncSettingChange(
                          "autoSync",
                          !syncSettings.autoSync,
                        )
                      }
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        syncSettings.autoSync
                          ? isDarkMode
                            ? "bg-[#7F56D9]"
                            : "bg-purple-600"
                          : isDarkMode
                            ? "bg-gray-600"
                            : "bg-gray-300"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          syncSettings.autoSync
                            ? "translate-x-6"
                            : "translate-x-1"
                        }`}
                      />
                    </button>
                  </label>

                  {/* Sync Interval */}
                  <div
                    className={`p-3 rounded-lg ${
                      isDarkMode ? "bg-[#0C0E12]" : "bg-white"
                    }`}
                  >
                    <label
                      className={`block font-medium mb-2 ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Sync Interval
                    </label>
                    <select
                      value={syncSettings.syncInterval}
                      onChange={(e) =>
                        handleSyncSettingChange("syncInterval", e.target.value)
                      }
                      disabled={!syncSettings.autoSync}
                      className={`w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                        isDarkMode
                          ? "bg-[#1a1d23] border-gray-600 text-white focus:border-[#7F56D9]"
                          : "bg-white border-gray-300 text-gray-900 focus:border-purple-500"
                      }`}
                    >
                      <option value="15min">Every 15 minutes</option>
                      <option value="30min">Every 30 minutes</option>
                      <option value="hourly">Every hour</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                    </select>
                    <p
                      className={`text-xs mt-2 ${
                        isDarkMode ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      How often data should sync automatically
                    </p>
                  </div>
                </div>
              </div>

              {/* Data Direction */}
              <div
                className={`border rounded-lg p-5 ${
                  isDarkMode
                    ? "bg-[#1a1d23] border-gray-700"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <h4
                  className={`font-medium mb-4 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Sync Direction
                </h4>

                <div className="space-y-3">
                  <label
                    className={`flex items-start gap-3 p-3 rounded-lg transition-colors cursor-pointer ${
                      isDarkMode
                        ? "bg-[#0C0E12] hover:bg-[#22262F]"
                        : "bg-white hover:bg-gray-100"
                    }`}
                  >
                    <input
                      type="radio"
                      name="syncDirection"
                      value="zoho_to_app"
                      checked={syncSettings.syncDirection === "zoho_to_app"}
                      onChange={(e) =>
                        handleSyncSettingChange("syncDirection", e.target.value)
                      }
                      className={`mt-1 w-4 h-4 focus:ring-[#7F56D9] focus:ring-offset-0 ${
                        isDarkMode
                          ? "border-gray-600 text-[#7F56D9] bg-[#1a1d23]"
                          : "border-gray-300 text-purple-600 bg-white"
                      }`}
                    />
                    <div className="flex-1">
                      <div
                        className={`font-medium ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        Zoho CRM → Your App
                      </div>
                      <div
                        className={`text-sm ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Only sync data from Zoho CRM to your app (one-way)
                      </div>
                    </div>
                  </label>

                  <label
                    className={`flex items-start gap-3 p-3 rounded-lg transition-colors cursor-pointer ${
                      isDarkMode
                        ? "bg-[#0C0E12] hover:bg-[#22262F]"
                        : "bg-white hover:bg-gray-100"
                    }`}
                  >
                    <input
                      type="radio"
                      name="syncDirection"
                      value="bidirectional"
                      checked={syncSettings.syncDirection === "bidirectional"}
                      onChange={(e) =>
                        handleSyncSettingChange("syncDirection", e.target.value)
                      }
                      className={`mt-1 w-4 h-4 focus:ring-[#7F56D9] focus:ring-offset-0 ${
                        isDarkMode
                          ? "border-gray-600 text-[#7F56D9] bg-[#1a1d23]"
                          : "border-gray-300 text-purple-600 bg-white"
                      }`}
                    />
                    <div className="flex-1">
                      <div
                        className={`font-medium ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        Bidirectional Sync
                      </div>
                      <div
                        className={`text-sm ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Sync data in both directions (two-way sync)
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Conflict Resolution */}
              <div
                className={`border rounded-lg p-5 ${
                  isDarkMode
                    ? "bg-[#1a1d23] border-gray-700"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <h4
                  className={`font-medium mb-4 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Conflict Resolution
                </h4>

                <div
                  className={`p-3 rounded-lg ${
                    isDarkMode ? "bg-[#0C0E12]" : "bg-white"
                  }`}
                >
                  <label
                    className={`block font-medium mb-2 ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    When conflicts occur
                  </label>
                  <select
                    value={syncSettings.conflictResolution || "zoho_wins"}
                    onChange={(e) =>
                      handleSyncSettingChange(
                        "conflictResolution",
                        e.target.value,
                      )
                    }
                    className={`w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                      isDarkMode
                        ? "bg-[#1a1d23] border-gray-600 text-white focus:border-[#7F56D9]"
                        : "bg-white border-gray-300 text-gray-900 focus:border-purple-500"
                    }`}
                  >
                    <option value="zoho_wins">
                      Zoho CRM data takes precedence
                    </option>
                    <option value="app_wins">
                      Your app data takes precedence
                    </option>
                    <option value="newest_wins">
                      Most recently updated wins
                    </option>
                    <option value="manual">Manual resolution required</option>
                  </select>
                  <p
                    className={`text-xs mt-2 ${
                      isDarkMode ? "text-gray-500" : "text-gray-400"
                    }`}
                  >
                    How to handle data conflicts during sync
                  </p>
                </div>
              </div>

              {/* Connection Info */}
              {integration && (
                <div
                  className={`rounded-lg p-4 ${
                    isDarkMode
                      ? "bg-blue-900/20 border border-blue-900/50"
                      : "bg-blue-50 border border-blue-200"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <AlertCircle
                      className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                        isDarkMode ? "text-blue-400" : "text-blue-600"
                      }`}
                    />
                    <div className="flex-1">
                      <div
                        className={`font-medium mb-1 ${
                          isDarkMode ? "text-blue-400" : "text-blue-600"
                        }`}
                      >
                        Connection Information
                      </div>
                      <div
                        className={`text-sm space-y-1 ${
                          isDarkMode ? "text-blue-300" : "text-blue-500"
                        }`}
                      >
                        <div>
                          Connected Account:{" "}
                          <span className="font-medium">
                            {integration.accountEmail || "N/A"}
                          </span>
                        </div>
                        <div>
                          Organization:{" "}
                          <span className="font-medium">
                            {integration.organizationName || "N/A"}
                          </span>
                        </div>
                        <div>
                          Data Center:{" "}
                          <span className="font-medium">
                            {integration.dataCenter || "N/A"}
                          </span>
                        </div>
                        <div>
                          Connected:{" "}
                          <span className="font-medium">
                            {integration.connectedAt
                              ? new Date(
                                  integration.connectedAt,
                                ).toLocaleString()
                              : "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className={`p-6 border-t flex items-center justify-end gap-3 ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <button
            onClick={onClose}
            disabled={saving}
            className={`px-6 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isDarkMode
                ? "bg-[#1a1d23] hover:bg-[#22262F] text-white"
                : "bg-gray-100 hover:bg-gray-200 text-gray-700"
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className={`px-6 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${
              isDarkMode
                ? "bg-[#7F56D9] hover:bg-[#6B46C1] text-white"
                : "bg-purple-600 hover:bg-purple-700 text-white"
            }`}
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Configuration
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ZohoConfigModal;
